import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Sheet,
  Typography,
} from '@mui/joy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LoginIcon from '@mui/icons-material/Login';
import HomeIcon from '@mui/icons-material/Home';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useAuth } from '../../main/context/AuthContext';
import { useSiteSettings } from '../../main/context/SiteSettingsContext';
import { getPublicPaySiteDisplayName } from '../../main/utils/siteDisplayName';
import { reconcilePaymentResult } from '../../main/API/publicPay';
import { dedupeRequest } from '../../main/utils/dedupeRequest';
import PublicBrandedPageShell from './PublicBrandedPageShell';

const RECONCILE_RETRY_DELAYS_MS = [3000, 8000, 15000];

function parsePaymobSuccess(searchParams) {
  const raw = searchParams.get('success');
  if (raw === null || raw === '') return null;
  const normalized = String(raw).toLowerCase();
  if (normalized === 'true' || normalized === '1') return true;
  if (normalized === 'false' || normalized === '0') return false;
  return null;
}

export default function PaymentResultScreen() {
  const { t, i18n } = useTranslation('ui');
  const [searchParams] = useSearchParams();
  const { settings } = useSiteSettings();
  const { isAuthenticated } = useAuth();
  const siteName = getPublicPaySiteDisplayName(settings, i18n.language);
  const siteIcon = settings?.site_icon && String(settings.site_icon).trim();

  const token = searchParams.get('token');
  const checkoutId = searchParams.get('checkout_id');
  const sessionId = searchParams.get('session_id');
  const paymobOrderId = searchParams.get('order');
  const paymobTransactionId = searchParams.get('id');
  const redirectStatus = searchParams.get('status');
  const paymobSuccess = parsePaymobSuccess(searchParams);

  const [loading, setLoading] = useState(true);
  const [resultStatus, setResultStatus] = useState(null);
  const [reconcileError, setReconcileError] = useState(false);
  const retriedRef = useRef(0);
  const retryTimerRef = useRef(null);

  const hintStatus = useMemo(() => {
    if (paymobSuccess === true || redirectStatus === 'success') return 'success';
    if (paymobSuccess === false || redirectStatus === 'failure') return 'failure';
    return 'pending';
  }, [paymobSuccess, redirectStatus]);

  const runReconcile = useCallback(async (retryIndex = 0) => {
    if (!token || !checkoutId) {
      setLoading(false);
      setResultStatus(hintStatus === 'success' ? 'paid' : hintStatus === 'failure' ? 'failed' : 'pending');
      return;
    }

    if (retryIndex === 0) {
      setLoading(true);
      setReconcileError(false);
    }

    const reconcileKey = [
      'pay-result-reconcile',
      token,
      checkoutId,
      sessionId || '',
      paymobOrderId || '',
      paymobTransactionId || '',
    ].join(':');

    try {
      const res = await dedupeRequest(reconcileKey, () =>
        reconcilePaymentResult({
          token,
          checkoutId,
          sessionId: sessionId || undefined,
          paymobOrderId: paymobOrderId || undefined,
          paymobTransactionId: paymobTransactionId || undefined,
        })
      );
      const status = res?.data?.status;
      setResultStatus(status || 'pending');
      if (status === 'pending' && retryIndex < RECONCILE_RETRY_DELAYS_MS.length) {
        retriedRef.current = retryIndex + 1;
        if (retryTimerRef.current) {
          window.clearTimeout(retryTimerRef.current);
        }
        retryTimerRef.current = window.setTimeout(() => {
          runReconcile(retryIndex + 1);
        }, RECONCILE_RETRY_DELAYS_MS[retryIndex]);
      }
    } catch (e) {
      setReconcileError(true);
      setResultStatus(hintStatus === 'success' ? 'paid' : hintStatus === 'failure' ? 'failed' : 'pending');
    } finally {
      if (retryIndex === 0) {
        setLoading(false);
      }
    }
  }, [token, checkoutId, sessionId, paymobOrderId, paymobTransactionId, hintStatus]);

  useEffect(() => {
    retriedRef.current = 0;
    if (retryTimerRef.current) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    runReconcile(0);
    return () => {
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [token, checkoutId, sessionId, paymobOrderId, paymobTransactionId, runReconcile]);

  const display = useMemo(() => {
    if (loading) {
      return {
        icon: <CircularProgress size="lg" sx={{ mb: 2 }} />,
        color: 'warning',
        title: t('paymentResult.confirmingPayment'),
        body: t('paymentResult.pendingBody'),
      };
    }

    if (resultStatus === 'paid' || resultStatus === 'already_paid') {
      return {
        icon: <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.500', mb: 2 }} />,
        color: 'success',
        title: t('paymentResult.successTitle'),
        body: t('paymentResult.successBody'),
      };
    }

    if (resultStatus === 'failed' || resultStatus === 'cancelled') {
      return {
        icon: <ErrorOutlineIcon sx={{ fontSize: 56, color: 'danger.500', mb: 2 }} />,
        color: 'danger',
        title: t('paymentResult.failureTitle'),
        body: t('paymentResult.failureBody'),
      };
    }

    if (reconcileError) {
      return {
        icon: <ErrorOutlineIcon sx={{ fontSize: 56, color: 'warning.500', mb: 2 }} />,
        color: 'warning',
        title: t('paymentResult.pendingTitle'),
        body: t('paymentResult.reconcileFailed'),
      };
    }

    return {
      icon: <HourglassEmptyIcon sx={{ fontSize: 56, color: 'warning.500', mb: 2 }} />,
      color: 'warning',
      title: t('paymentResult.pendingTitle'),
      body: t('paymentResult.pendingBody'),
    };
  }, [loading, resultStatus, reconcileError, t]);

  const loginFooter = (
    <Box sx={{ mt: 3, textAlign: 'center' }}>
      <Button
        component={RouterLink}
        to={isAuthenticated ? '/customer-home' : '/customer-login'}
        size="lg"
        variant="solid"
        color="primary"
        startDecorator={isAuthenticated ? <HomeIcon /> : <LoginIcon />}
        sx={{ minWidth: 220, mb: isAuthenticated ? 1.5 : 0 }}
      >
        {isAuthenticated ? t('nav.home') : t('auth.customerLogin')}
      </Button>
      {isAuthenticated ? (
        <Box>
          <Button
            component={RouterLink}
            to="/customer-invoices"
            size="md"
            variant="soft"
            color="primary"
            startDecorator={<ReceiptLongIcon />}
            sx={{ minWidth: 220 }}
          >
            {t('paymentResult.viewInvoices')}
          </Button>
        </Box>
      ) : null}
      {!isAuthenticated ? (
        <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 1.5 }}>
          {t('paymentResult.customerLoginHint')}
        </Typography>
      ) : null}
    </Box>
  );

  return (
    <PublicBrandedPageShell
      siteIcon={siteIcon}
      siteName={siteName}
      pageTitle={t('paymentResult.pageTitle')}
      footer={!loading ? loginFooter : null}
    >
      <Sheet variant="outlined" sx={{ borderRadius: '14px', p: { xs: 2, sm: 3 } }}>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>{display.icon}</Box>
          <Typography level="title-lg" sx={{ mb: 1, fontWeight: 700 }}>
            {display.title}
          </Typography>
          <Alert color={display.color} variant="soft" sx={{ textAlign: 'start' }}>
            {display.body}
          </Alert>
        </Box>
      </Sheet>
    </PublicBrandedPageShell>
  );
}
